import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";

const starter = `type Report = {
  valid: number;
  rejected: number;
  byDepartment: Record<string, number>;
  mostExperienced: string | null;
  averageExperience: number;
};

/** Summarise an export that nobody has validated. */
function summarise(csv: string): Report {
  // TODO
  return { valid: 0, rejected: 0, byDepartment: {}, mostExperienced: null, averageExperience: 0 };
}
`;

const solution = `type Report = {
  valid: number;
  rejected: number;
  byDepartment: Record<string, number>;
  mostExperienced: string | null;
  averageExperience: number;
};

type Row = { name: string; department: string; years: number };

function summarise(csv: string): Report {
  const lines = csv.split("\\n").map((l) => l.trim()).filter((l) => l.length > 0);
  const body = lines.slice(1);   // the first line that has anything on it is the header

  const rows: Row[] = [];
  let rejected = 0;

  for (const line of body) {
    const cells = line.split(",").map((c) => c.trim());
    if (cells.length !== 3) { rejected++; continue; }

    const [name, department, rawYears] = cells;

    // The empty check has to come first. Number("") is 0, not NaN, so an export
    // with a blank years column would otherwise read as a room full of graduates.
    if (!name || !department || !rawYears) { rejected++; continue; }

    // Number, not parseInt: parseInt("5 years") is 5, which is a guess dressed up
    // as a fact. A field that is not a number is a broken row, not a five.
    const years = Number(rawYears);
    if (!Number.isFinite(years) || years < 0) { rejected++; continue; }

    rows.push({ name, department, years });
  }

  // A prototype-free object, because the department name is free text typed by a
  // human. On a plain {}, a department called "toString" reads back as the
  // inherited function instead of undefined and the count comes out as a string.
  const byDepartment: Record<string, number> = Object.create(null);
  for (const row of rows) byDepartment[row.department] = (byDepartment[row.department] ?? 0) + 1;

  let top: Row | null = null;
  for (const row of rows) {
    if (!top || row.years > top.years || (row.years === top.years && row.name < top.name)) top = row;
  }

  const total = rows.reduce((sum, row) => sum + row.years, 0);
  const averageExperience = rows.length === 0 ? 0 : Math.round((total / rows.length) * 10) / 10;

  return {
    valid: rows.length,
    rejected,
    byDepartment,
    mostExperienced: top ? top.name : null,
    averageExperience,
  };
}`;

const harness = HARNESS_KIT + `
if (typeof summarise !== "function") {
  throw new Error("summarise is not defined. Keep the function name as given.");
}

function __csv(rows) {
  return ["name,department,years"].concat(rows).join("\\n");
}

function __report(rows) {
  return function () { return summarise(__csv(rows)); };
}

function __field(rows, key) {
  return function () { return summarise(__csv(rows))[key]; };
}

const __tests = [
  {
    name: "summarises a clean export",
    about: "The basic contract, before any of the data goes wrong.",
    run: function () {
      var rows = ["Ada,Research,12", "Grace,Research,9", "Linus,Platform,20"];
      __expectExact("the worked example", __report(rows), {
        valid: 3,
        rejected: 0,
        byDepartment: { Research: 2, Platform: 1 },
        mostExperienced: "Linus",
        averageExperience: 13.7
      });
    }
  },
  {
    name: "counts the rejected rows instead of hiding them",
    about: "A summary that silently drops bad rows tells you the export was clean. The count is how anyone finds out it was not.",
    run: function () {
      var rows = ["Ada,Research,12", "Grace,Research,many", "Linus,Platform,20"];
      __expectExact("one unparseable row: valid", __field(rows, "valid"), 2);
      __expectExact("one unparseable row: rejected", __field(rows, "rejected"), 1);
    }
  },
  {
    name: "only valid rows reach the department counts",
    about: "A row rejected for a broken years column must not still be counted against its department, or the two numbers disagree.",
    run: function () {
      var rows = ["Ada,Research,12", "Grace,Research,", "Linus,Platform,20"];
      __expectExact("a blank years column", __field(rows, "byDepartment"), { Research: 1, Platform: 1 });
    }
  },
  {
    name: "an empty years field is not zero",
    hidden: true,
    about: "Number(\\"\\") is 0 and Number(\\" \\") is 0. Checking for NaN alone lets a blank column through as a graduate with no experience.",
    run: function () {
      __expectExact("blank years", __field(["Ada,Research,"], "rejected"), 1);
      __expectExact("whitespace years", __field(["Ada,Research,   "], "rejected"), 1);
      __expectExact("... and neither counts as valid", __field(["Ada,Research,"], "valid"), 0);
    }
  },
  {
    name: "a number with a unit stuck to it is rejected",
    hidden: true,
    about: "parseInt(\\"5 years\\") is 5. That is a guess about what somebody meant, presented to the caller as a measurement.",
    run: function () {
      __expectExact("years given as '5 years'", __field(["Ada,Research,5 years"], "rejected"), 1);
      __expectExact("years given as '12+'", __field(["Ada,Research,12+"], "rejected"), 1);
      __expectExact("a negative number", __field(["Ada,Research,-3"], "rejected"), 1);
    }
  },
  {
    name: "cells are trimmed, so spacing is not a second department",
    hidden: true,
    about: "Exports pad columns. ' Research ' and 'Research' splitting the count in two is the kind of error nobody notices until a total is wrong.",
    run: function () {
      var rows = ["Ada , Research , 12", "Grace,Research,9"];
      __expectExact("padded cells", __field(rows, "byDepartment"), { Research: 2 });
      __expectExact("... and the name is trimmed too", __field(rows, "mostExperienced"), "Ada");
    }
  },
  {
    name: "a row with the wrong number of columns is rejected",
    hidden: true,
    about: "A missing or extra comma shifts every field along. Reading position 2 regardless gives a department in the years column.",
    run: function () {
      __expectExact("two columns", __field(["Ada,Research"], "rejected"), 1);
      __expectExact("four columns", __field(["Ada,Research,12,extra"], "rejected"), 1);
    }
  },
  {
    name: "blank lines are ignored, not rejected",
    hidden: true,
    about: "A trailing newline is not a broken record. Counting it inflates the rejected number every single time.",
    run: function () {
      __expectExact("a trailing blank line: rejected", __field(["Ada,Research,12", "", "  "], "rejected"), 0);
      __expectExact("a trailing blank line: valid", __field(["Ada,Research,12", "", "  "], "valid"), 1);
    }
  },
  {
    name: "ties on experience break by name",
    hidden: true,
    about: "Without a tie-break the answer depends on row order, so re-exporting the same data changes who the report names.",
    run: function () {
      __expectExact("Zoe and Ada both on 12", __field(["Zoe,Platform,12", "Ada,Research,12"], "mostExperienced"), "Ada");
    }
  },
  {
    name: "an export with nothing valid in it still answers",
    hidden: true,
    about: "Dividing by zero rows gives NaN, which serialises to null and looks like a missing field rather than an empty file.",
    run: function () {
      __expectExact("every row broken", __report(["Ada,Research,x", "bad line"]), {
        valid: 0,
        rejected: 2,
        byDepartment: {},
        mostExperienced: null,
        averageExperience: 0
      });
    }
  },
  {
    name: "the average is rounded to one decimal",
    hidden: true,
    about: "Floating point gives 13.666666666666666 for three rows. A report is read by a person.",
    run: function () {
      __expectExact("12, 9 and 20", __field(["Ada,R,12", "Grace,R,9", "Linus,P,20"], "averageExperience"), 13.7);
      __expectExact("10 and 5", __field(["Ada,R,10", "Grace,R,5"], "averageExperience"), 7.5);
    }
  },
  {
    name: "a department can be named anything a human can type",
    hidden: true,
    about: "The department is free text used as a key. On a plain object, a value inherited from the prototype reads back instead of undefined and the count becomes a string.",
    run: function () {
      var rows = ["Ada,toString,12", "Grace,toString,9", "Linus,constructor,20"];
      __expectExact("departments called toString and constructor",
        __field(rows, "byDepartment"), { toString: 2, constructor: 1 });
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "Before you write any of the parsing, ask yourself what Number(\"\") returns. Every gate below has to run before the conversion, not after it.",
    code: `const cells = line.split(",").map((c) => c.trim());
if (cells.length !== 3) { rejected++; continue; }

const [name, department, rawYears] = cells;

// This check has to come FIRST. Number("") is 0, not NaN.
if (!name || !department || !rawYears) { rejected++; continue; }

// Number, not parseInt: parseInt("5 years") is 5, a guess dressed as a fact.
const years = Number(rawYears);
if (!Number.isFinite(years) || years < 0) { rejected++; continue; }`,
  },
  {
    label: "Approach",
    body: "Split into lines, drop the blank ones, skip the header, validate each row on its own, and collect the survivors. Aggregate only over those, and count what did not make it. The tie-break on the name is what stops a re-export changing who the report names.",
    code: `const lines = csv.split("\\n").map((l) => l.trim()).filter((l) => l.length > 0);
const body = lines.slice(1);   // first non-blank line is the header

// Prototype-free: the department is free text a human typed, and on a plain {}
// a department called "toString" reads back as the inherited function.
const byDepartment: Record<string, number> = Object.create(null);
for (const row of rows) byDepartment[row.department] = (byDepartment[row.department] ?? 0) + 1;

let top: Row | null = null;
for (const row of rows) {
  if (!top || row.years > top.years || (row.years === top.years && row.name < top.name)) top = row;
}`,
  },
  {
    label: "The trap",
    body: "Number(\"\") is 0 and parseInt(\"5 years\") is 5, so both bad rows turn into plausible data unless you reject them before converting. A blank trailing line is not a broken record and must not inflate the rejected count. And with no valid rows at all, dividing by zero gives NaN, which serialises to null and reads as a missing field.",
  },
];

export const csvEmployeeReport: Drill = {
  id: "csv-employee-report",
  track: "typescript",
  title: "Summarise a CSV nobody validated",
  pattern: "defensive-parsing",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  statement: `An HR system exports staff as CSV and the export is not clean. Summarise it without inventing anything.

\`\`\`ts
type Report = {
  valid: number;
  rejected: number;
  byDepartment: Record<string, number>;
  mostExperienced: string | null;
  averageExperience: number;
};

function summarise(csv: string): Report;
\`\`\`

The first line with anything on it is the header, \`name,department,years\`.

## Requirements

- **A row is valid** only when name and department are non-empty and years is a finite number of at least zero.
- **Everything else is rejected and counted**, never silently dropped.
- **Cells are trimmed.** Blank lines are ignored rather than rejected.
- **Only valid rows** feed the department counts, the average and the most experienced name.
- **Ties on years break by name**, ascending. The average is rounded to one decimal. Nothing valid gives \`null\` and \`0\`.

## Example

\`\`\`ts
summarise(\`name,department,years
Ada,Research,12
Grace,Research,9
Linus,Platform,20\`);

// {
//   valid: 3, rejected: 0,
//   byDepartment: { Research: 2, Platform: 1 },
//   mostExperienced: "Linus", averageExperience: 13.7
// }
\`\`\`

## Where this gets interesting

The parsing is not the hard part. The hard part is that JavaScript is enthusiastic about turning bad data into plausible numbers, and it does it quietly. \`Number("")\` is \`0\`, not \`NaN\`, so a blank years column reads as a graduate. \`parseInt("5 years")\` is \`5\`, so a typed-in unit becomes a measurement. Both produce a report that is confidently wrong, which is worse than one that admits the row was broken.

Then there is the department name, which is free text a human typed and which you are about to use as an object key. That is the same class of problem one layer down.`,
  hints,
  starter,
  harness,
  solution,
  complexity: "One pass over the rows, O(n) time and O(d) space for the distinct departments.",
};
